import type { IntlShape } from 'react-intl'
import moment from 'moment'

import { getRecentSearchDescription } from 'components/Search/utils'
import type { DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import {
  DISCOVER_NEAR_CURATED_CITIES,
  EXP_CATEGORY_LUX_PLUS_ID,
  type DiscoverNearDestinationConfig,
} from 'constants/experience'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import { arrayToObject, groupBy, sortByAll, sum } from 'lib/array/arrayUtils'
import {
  getSearchItemDestinationId,
  getSearchItemDisplayName,
  getSearchItemExperienceId,
  isExperienceSearchItem,
} from 'lib/search/searchItem'

export type ExperiencesHomepageSuggestedSearchSource = 'global' | 'experience'

export interface ExperiencesHomepageSuggestedSearchView {
  recentSearch: App.FullRecentSearch
  source: ExperiencesHomepageSuggestedSearchSource
  title: string
  primaryCaption: string
  secondaryCaption?: string
  to: string
}

function isWithinLastDays(dateAdded: string, days: number, now: Date = new Date()): boolean {
  const added = moment(dateAdded)
  if (!added.isValid()) {
    return false
  }

  return added.isSameOrAfter(moment(now).subtract(days, 'days'), 'day')
}

function getSearchPlaceId(searchItem: App.SearchItem): string | undefined {
  return getSearchItemDestinationId(searchItem)
}

function getSearchPlaceName(searchItem: App.SearchItem): string {
  return getSearchItemDisplayName(searchItem)
}

function isFlightRecentSearch(recentSearch: App.FullRecentSearch): boolean {
  const vertical = recentSearch.vertical?.toLowerCase()
  return vertical === 'flights' || vertical === 'flight'
}

export function getExperiencesHomepageRecentSearchUrl(
  recentSearch: App.FullRecentSearch,
  categories?: Array<number>,
): string {
  const { searchItem, dates } = recentSearch
  const destinationId = getSearchPlaceId(searchItem)
  const destinationName = getSearchPlaceName(searchItem)

  const query = replaceExperienceQueryString({
    destinationId: isExperienceSearchItem(searchItem) ? undefined : destinationId,
    destinationName: isExperienceSearchItem(searchItem) ? undefined : destinationName,
    experienceId: isExperienceSearchItem(searchItem) ? getSearchItemExperienceId(searchItem) : undefined,
    experienceName: isExperienceSearchItem(searchItem) ? destinationName : undefined,
    checkIn: dates.checkIn,
    checkOut: dates.checkOut,
    categories,
  })

  return `/search/experiences?${query}`
}

export function getExperiencesHomepageSuggestedSearchCaptions(
  intl: IntlShape,
  recentSearch: App.FullRecentSearch,
  categoryNamesById?: ReadonlyMap<number, string>,
): { primaryCaption: string, secondaryCaption?: string } {
  const { dates } = recentSearch
  const primaryCaption = getRecentSearchDescription(
    intl,
    dates.checkIn,
    dates.checkOut,
    dates.flexibleMonths,
    dates.flexibleDuration as DEFAULT_FLEXIBLE_DURATION_RANGE,
    dates.isAnytime,
  )

  const categoryIds = recentSearch.categories ?? []
  const categoryNames = categoryIds.map(categoryId => categoryNamesById?.get(categoryId))

  if (isFlightRecentSearch(recentSearch) || !categoryIds.length || categoryNames.some(name => !name)) {
    return { primaryCaption }
  }

  const [firstCategoryName] = categoryNames as Array<string>

  return {
    primaryCaption,
    secondaryCaption: categoryNames.length === 1 ? firstCategoryName : intl.formatMessage(
      {
        id: 'swGKg75ACanOUULtAfu-',
        defaultMessage: '{firstCategoryName} + {additionalCategoryCount} more',
        description: 'Secondary caption on an Experiences homepage suggested search with multiple saved category filters',
      },
      {
        firstCategoryName,
        additionalCategoryCount: categoryNames.length - 1,
      },
    ),
  }
}

function getSuggestedSearchDedupeKey(recentSearch: App.FullRecentSearch): string {
  return `${recentSearch.searchItem.searchType}:${recentSearch.searchItem.value}`
}

export function mergeExperiencesHomepageRecentSearchSources(
  globalRecentSearches: Array<App.FullRecentSearch>,
  experienceRecentSearches: Array<App.FullRecentSearch>,
): Array<{ recentSearch: App.FullRecentSearch, source: ExperiencesHomepageSuggestedSearchSource }> {
  const merged = new Map<string, { recentSearch: App.FullRecentSearch, source: ExperiencesHomepageSuggestedSearchSource }>()

  const addSearch = (
    recentSearch: App.FullRecentSearch,
    source: ExperiencesHomepageSuggestedSearchSource,
  ) => {
    const key = getSuggestedSearchDedupeKey(recentSearch)
    const existing = merged.get(key)

    if (!existing || moment(recentSearch.dateAdded).isAfter(existing.recentSearch.dateAdded)) {
      merged.set(key, { recentSearch, source })
    }
  }

  globalRecentSearches.forEach(search => addSearch(search, 'global'))
  experienceRecentSearches.forEach(search => addSearch(search, 'experience'))

  return Array.from(merged.values())
}

export function getExperiencesHomepageSuggestedSearches(
  intl: IntlShape,
  {
    globalRecentSearches,
    experienceRecentSearches,
    excludedPlaceIds,
    excludedPlaceNames,
    experienceCategories,
    now,
  }: {
    globalRecentSearches: Array<App.FullRecentSearch>
    experienceRecentSearches: Array<App.FullRecentSearch>
    excludedPlaceIds?: Array<string>
    excludedPlaceNames?: Array<string>
    experienceCategories: Array<App.ExperienceItemCategory>
    now?: Date
  },
): Array<ExperiencesHomepageSuggestedSearchView> {
  const mergedSearches = mergeExperiencesHomepageRecentSearchSources(
    globalRecentSearches,
    experienceRecentSearches,
  )
  const sourceByRecentSearchId = new Map(
    mergedSearches.map(({ recentSearch, source }) => [recentSearch.recentSearchId, source]),
  )
  const categoryNamesById = new Map(
    experienceCategories.map(category => [category.id, category.name]),
  )

  return mapRecentSearchesToExperiencesHomepageSuggestedSearches(
    intl,
    mergedSearches.map(({ recentSearch }) => recentSearch),
    { excludedPlaceIds, excludedPlaceNames, now, sourceByRecentSearchId, categoryNamesById },
  ).map((item) => ({
    ...item,
    source: sourceByRecentSearchId.get(item.recentSearch.recentSearchId) ?? 'global',
  }))
}

export const EXPERIENCES_HOMEPAGE_RECENT_SEARCH_MAX_AGE_DAYS = 30

export function mapRecentSearchesToExperiencesHomepageSuggestedSearches(
  intl: IntlShape,
  recentSearches: Array<App.FullRecentSearch>,
  options: {
    excludedPlaceIds?: Array<string>
    excludedPlaceNames?: Array<string>
    sourceByRecentSearchId?: ReadonlyMap<string, ExperiencesHomepageSuggestedSearchSource>
    categoryNamesById?: ReadonlyMap<number, string>
    now?: Date
  } = {},
): Array<ExperiencesHomepageSuggestedSearchView> {
  const excludedPlaceIds = new Set((options.excludedPlaceIds ?? []).filter(Boolean))
  const excludedPlaceNames = new Set(
    (options.excludedPlaceNames ?? [])
      .filter(Boolean)
      .map(name => name.trim().toLowerCase()),
  )

  return recentSearches
    .filter(search => isWithinLastDays(search.dateAdded, EXPERIENCES_HOMEPAGE_RECENT_SEARCH_MAX_AGE_DAYS, options.now))
    .slice()
    .sort((a, b) => moment(b.dateAdded).valueOf() - moment(a.dateAdded).valueOf())
    .filter((search) => {
      const placeId = getSearchPlaceId(search.searchItem)
      if (placeId && excludedPlaceIds.has(placeId)) {
        return false
      }

      const placeName = getSearchPlaceName(search.searchItem).trim().toLowerCase()
      if (placeName && excludedPlaceNames.has(placeName)) {
        return false
      }

      return true
    })
    .map((recentSearch) => {
      const source = options.sourceByRecentSearchId?.get(recentSearch.recentSearchId) ?? 'global'
      const categories = source === 'experience' ? recentSearch.categories : undefined
      const captions = getExperiencesHomepageSuggestedSearchCaptions(
        intl,
        { ...recentSearch, categories },
        options.categoryNamesById,
      )

      return {
        recentSearch,
        source,
        title: getSearchPlaceName(recentSearch.searchItem),
        primaryCaption: captions.primaryCaption,
        secondaryCaption: captions.secondaryCaption,
        to: getExperiencesHomepageRecentSearchUrl(recentSearch, categories),
      }
    })
}

export function getExperienceCollectionFacetCount(
  collections: ReadonlyArray<App.ExperienceCollectionFacet>,
  predicate: (collection: App.ExperienceCollectionFacet) => boolean,
): number {
  return collections.find(predicate)?.count ?? 0
}

export function getExperienceCategoryFacetCount(
  categories: ReadonlyArray<App.ExperienceCategoryFacet>,
  categoryId: number,
): number {
  return categories.find((category) => category.categoryId === categoryId)?.count ?? 0
}

export function getExperiencesHomepageVerifiedExperiencesCount(count: number): number {
  const wholeCount = Math.max(0, Math.floor(count))

  if (wholeCount < 10) {
    return Math.max(0, wholeCount - 1)
  }

  const unit = 10 ** Math.floor(Math.log10(wholeCount))
  return Math.floor(wholeCount / unit) * unit
}

export interface ExperiencePrimaryCategoryData {
  parent: App.ExperienceItemCategory;
  children: Array<App.ExperienceItemCategory>;
  count: number;
  relevanceScore: number;
}

export type CategoryData = ExperiencePrimaryCategoryData

interface TopLevelCategoryFacet {
  cat: App.ExperienceItemCategory;
  enabled: boolean;
  count: number;
  relevanceScore: number;
  selected: boolean;
}

export function buildPrimaryExperienceCategories({
  categories,
  categoriesFacets,
  isLuxPlusEnabled,
  rankCategoriesByRelevance = false,
  prioritiseSelectedCategories = false,
  selectedCategoryCodes = [],
  luxPlusMemberPriceCount = 0,
  useParentCategoryCounts = false,
}: {
  categories: Array<App.ExperienceItemCategory>;
  categoriesFacets: Array<App.ExperienceCategoryFacet>;
  isLuxPlusEnabled: boolean;
  rankCategoriesByRelevance?: boolean;
  prioritiseSelectedCategories?: boolean;
  selectedCategoryCodes?: Array<number>;
  luxPlusMemberPriceCount?: number;
  useParentCategoryCounts?: boolean;
}): Array<ExperiencePrimaryCategoryData> {
  if (categoriesFacets.length == 0 && luxPlusMemberPriceCount == 0 && (!prioritiseSelectedCategories || selectedCategoryCodes.length == 0)) {
    return []
  }

  const childToParentCategories = groupBy(
    categories.filter((cat) => cat.parentId),
    (cat) => cat.parentId,
  )
  const categoryFacetsById = arrayToObject(
    categoriesFacets,
    (cat) => cat.categoryId,
  )

  const topLevelCategories = categories.filter(
    (cat) => cat.level === 'parent',
  )
  const topLevelCategoryFacets = topLevelCategories.map<TopLevelCategoryFacet>((cat) => {
    const childCategories = childToParentCategories.get(cat.id) ?? []
    const relatedCategories = [cat, ...childCategories]
    const isLuxPlusCategory = cat.id === EXP_CATEGORY_LUX_PLUS_ID
    const childCategoryCount = sum(
      childCategories,
      (childCat) => categoryFacetsById[childCat.id]?.count ?? 0,
    )
    return {
      cat,
      enabled: !isLuxPlusCategory || isLuxPlusEnabled,
      count: isLuxPlusCategory
        ? luxPlusMemberPriceCount ?? 0
        : useParentCategoryCounts
          ? categoryFacetsById[cat.id]?.count ?? childCategoryCount
          : childCategoryCount,
      relevanceScore: Math.max(
        0,
        ...relatedCategories.map(
          (relatedCategory) =>
            categoryFacetsById[relatedCategory.id]?.relevanceScore ?? 0,
        ),
      ),
      selected: prioritiseSelectedCategories && relatedCategories.some((category) => selectedCategoryCodes.includes(category.id)),
    }
  })

  const sortedCategories = sortByAll(
    topLevelCategoryFacets,
    [
      ...(prioritiseSelectedCategories ? [{ selector: (val: TopLevelCategoryFacet) => val.selected ? 1 : 0, direction: 'desc' } as const] : []),
      ...(rankCategoriesByRelevance ? [{ selector: (val: TopLevelCategoryFacet) => val.relevanceScore, direction: 'desc' } as const] : []),
      { selector: (val) => val.count, direction: 'desc' },
    ],
  )

  return sortedCategories
    .filter((fac) => (fac.count !== 0 || (prioritiseSelectedCategories && fac.selected)) && fac.enabled)
    .map((fac) => ({
      parent: fac.cat,
      children: childToParentCategories.get(fac.cat.id) ?? [],
      count: fac.count,
      relevanceScore: fac.relevanceScore,
    }))
}

export type DiscoverNearDestinationView = {
  placeId: string
  name: string
  imageId: string
  type: string
  countryCode?: string
}

export function isDiscoverNearCuratedAnchor(anchorPlaceId: string): boolean {
  return anchorPlaceId in DISCOVER_NEAR_CURATED_CITIES
}

function mapCuratedEntryToPlaceholderDestination(
  entry: DiscoverNearDestinationConfig,
): DiscoverNearDestinationView {
  return {
    placeId: entry.placeId,
    name: entry.name,
    imageId: entry.imageId,
    type: entry.type ?? 'high_level_region',
    countryCode: entry.countryCode,
  }
}

export function getDiscoverNearDestinations(
  anchorPlaceId: string,
): Array<DiscoverNearDestinationView> {
  const curatedDestinations = DISCOVER_NEAR_CURATED_CITIES[anchorPlaceId]

  if (!curatedDestinations) {
    return []
  }

  return curatedDestinations.map(mapCuratedEntryToPlaceholderDestination)
}
